import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useFormik } from 'formik';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Address as AddressType } from '@commercetools/platform-sdk';
import classnames from 'classnames';
import statesOptions from '../../../components/forms/AddressForm/states.json';
import validator from './validator';

import Address from '../../../components/forms/AddressForm';
import AddressSummary from '../../../components/AddressSummary';
import PrimaryButton from '../../../components/common/PrimaryButton';
import Checkbox from '../../../components/common/Checkbox';

import cartService from '../../../services/cart';

import styles from './ShippingInfoStep.module.scss';
import messages from './messages';
import commonMessages from '../messages';
import {
  isSameAddress,
  focusAndScrollToByName,
  isDuplicateAddress,
} from '../../../utils';
import { handleHeapErrorHandling } from '../../../utils/heap';

import AddressValidatorModal from '../../../components/AddressValidatorModal';
import ErrorBanner from '../../../components/common/ErrorBanner';
import useSteppedCheckoutError from '../../../hooks/useSteppedCheckoutError';
import drupalPubSubService from '../../../services/drupalPubSub';
import {
  SHIPPING_ADDRESS_STATE_NOT_ALLOWED,
  SLEEP_COUNTRY_CANADA,
  SLEEP_COUNTRY_CANADA_URL,
} from '../SteppedCheckout/constants';
import SecondaryButton from '../../../components/common/SecondaryButton';
import useQueryParams from '../../../hooks/useQueryParams';

import { setNeedsToValidateFieldsOfCustomerAccountAddressDraft } from '../../../slices/shippingAddress/index.migrated';
import { CartInStore } from '../../../slices/cart/types';
import { AccountsInStore } from '../../../slices/accounts/types';
import {
  CustomerAddress,
  ShippingAddressOnStore,
} from '../../../slices/shippingAddress/types';
import { ValidateAddressResponse } from '../../../services/cart/types';
import { PaymentsInStore } from '../../../slices/payments/types';
import useTealium from '../../../hooks/useTealium/useTealium';


const NO_CHANGES_VALUE = 'NO_CHANGES';
const VALID_ADDRESS_SCORE = '100';
const ADDRESS_PROPERTY_COUNTRY = 'country';
const EMAIL_PROPERTY = 'email';

const isCountryStateNotAllowedError = (errorCode: string) =>
  errorCode === SHIPPING_ADDRESS_STATE_NOT_ALLOWED;

const ShippingInfoStep = ({
  showCompleted,
  isCompleted = false,
  showEditing,
  onClickSave,
  onClickCancel,
  shippingAddress,
  isEditing = false,
}: {
  showCompleted: boolean;
  isCompleted?: boolean;
  showEditing: boolean;
  onClickSave: (address: CustomerAddress) => Promise<void>;
  onClickCancel: () => Promise<void>;
  shippingAddress: CustomerAddress;
  isEditing?: boolean;
}) => {
  const { publish } = drupalPubSubService;
  const { formatMessage } = useIntl();
  const [validateAddress, setValidateAddress] =
    useState<ValidateAddressResponse | null>(null);
  const {
    cartId,
    isCartLoading,
    customerId,
    customerAddress,
    addresses,
    needsToValidateFieldsOfCustomerAccountAddressDraft,
  } = useSelector<{
    cart: CartInStore;
    isCartLoading: boolean;
    accounts: AccountsInStore;
    shippingAddress: ShippingAddressOnStore;
  }>((state) => ({
    cartId: state.cart.id,
    isCartLoading: state.cart.status === 'loading',
    customerId: state.accounts?.customerId,
    customerAddress: state.accounts?.customerAddress,
    addresses: state.accounts?.addresses,
    needsToValidateFieldsOfCustomerAccountAddressDraft:
      state.shippingAddress?.needsToValidateFieldsOfCustomerAccountAddressDraft,
  })) as {
    cartId: string;
    isCartLoading: boolean;
    customerId: string;
    customerAddress: AddressType;
    addresses: AddressType[];
    needsToValidateFieldsOfCustomerAccountAddressDraft: boolean;
  };
  const [isSaving, setIsSaving] = useState(false);
  const [signupForNewsletter, setSignupForNewsletter] = useState(true);
  const dispatch = useDispatch();

  const { isShippingStepError, errorCode, errorMessage } =
    useSteppedCheckoutError();

  const [amazonPayApproved, amazonPayCheckoutCreated] = useQueryParams([
    'amazonPayApproved',
    'amazonPayCheckoutCreated',
  ]);

  const { paymentMethodData, paypalOrderId, applePaymentData } = useSelector<{
    payments: PaymentsInStore;
  }>((state) => state.payments) as {
    paymentMethodData: Record<string, string>;
    paypalOrderId: string;
    applePaymentData: string;
  };

  const showShopPayEmailCapture = window?.purTogglesEnabled(
    'shop_pay_email_field',
  );

  const isMobile = window.innerWidth < 768;

  const shopPaySignupNewsletterContainerElementId = isMobile
    ? 'shop-pay-signup-newsletter-container-mobile'
    : 'shop-pay-signup-newsletter-container-desktop';

  const signupForNewsletterPortalElement = document.getElementById(
    shopPaySignupNewsletterContainerElementId,
  ) as HTMLElement;

  const isExpressCheckoutPayment =
    (!!paymentMethodData && Object.keys(paymentMethodData).length > 0) ||
    !!paypalOrderId ||
    !!amazonPayCheckoutCreated ||
    !!amazonPayApproved ||
    !!applePaymentData;

  const [errorOnValidateAddress, setErrorOnValidateAddress] = useState(false);

  const onSubmit = useCallback(
    async (values: CustomerAddress) => {
      setIsSaving(true);

      // TODO: Move this logic to detect new/updated address to the save method on step checkout for the 1st step
      if (customerId) {
        // shippingAddress can be pulled from cart-ms or user's primary address.
        // If it's not a primary address, must be added to user's addresses list on order submit.
        if (!addresses?.length || !isDuplicateAddress(values, addresses)) {
          localStorage.setItem('newAddress', 'true');
          localStorage.removeItem('updateAddress');
          localStorage.removeItem('shippingAddressId');
        } else if (
          shippingAddress.isDefaultShippingAddress &&
          shippingAddress.phone !== values.phone
        ) {
          localStorage.removeItem('newAddress');
          localStorage.setItem('updateAddress', 'true');
          localStorage.setItem(
            'shippingAddressId',
            shippingAddress?.id?.toString() ?? '',
          );
        }
      }

      const data = {
        listID: 'subscribe_welcome_popup',
        authData: {
          email: values.email,
        },
        channels: {
          email: values.email,
        },
      };

      if (!isSameAddress(shippingAddress, values)) {
        try {
          const validateAddressResponse = (await cartService.validateAddress({
            ...values,
            cartId,
          })) as ValidateAddressResponse;

          if (
            !validateAddressResponse.fedexChanges.filter(
              (fedexChange: { text: string }) =>
                fedexChange.text !== NO_CHANGES_VALUE,
            ).length &&
            validateAddressResponse.score === VALID_ADDRESS_SCORE
          ) {
            if (signupForNewsletter) {
              publish({ subscription: 'onEmailSubscribe', parameters: data });
            }
            onClickSave(values);
          } else {
            setValidateAddress(validateAddressResponse);
          }
        } catch (error) {
          setErrorOnValidateAddress(true);
        }
      } else {
        if (signupForNewsletter) {
          publish({ subscription: 'onEmailSubscribe', parameters: data });
        }
        onClickSave(values);
      }
      setIsSaving(false);
    },
    [
      cartId,
      addresses,
      customerId,
      onClickSave,
      publish,
      shippingAddress,
      signupForNewsletter,
    ],
  );

  const initialValues: CustomerAddress = {
    email: customerAddress?.email ?? '',
    phone: '',
    firstName: '',
    lastName: '',
    streetAddress: '',
    additionalStreetInfo: '',
    postalCode: '',
    city: '',
    country: 'US',
    state: statesOptions[0].value,
  };

  const {
    handleChange,
    values,
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    resetForm,
    isValid,
    validateForm,
  } = useFormik({
    initialValues,
    onSubmit: async (formValues) => {
      await onSubmit(formValues);
    },
    validate: (v) =>
      validator(v, { formatMessage, isShowingContactDetails: true }),
  });

  const normalizeAddress: CustomerAddress = {
    ...initialValues,
    ...shippingAddress,
    streetAddress: shippingAddress.streetName ?? '',
    email: customerAddress?.email ?? shippingAddress.email,
  };

  const normalizeAddressAsJSON = JSON.stringify(normalizeAddress);

  useEffect(() => {
    let normalizeAddressValue: CustomerAddress;
    let normalizeAddressIsEmpty = true;

    if (normalizeAddressAsJSON) {
      normalizeAddressValue = JSON.parse(
        normalizeAddressAsJSON,
      ) as CustomerAddress;

      normalizeAddressIsEmpty = Object.entries(normalizeAddressValue)
        .filter(
          ([propertyName]) =>
            propertyName !== ADDRESS_PROPERTY_COUNTRY &&
            (!customerAddress?.email || propertyName !== EMAIL_PROPERTY),
        )
        .every(([, propertyValue]) =>
          [undefined, ''].includes(propertyValue as string),
        );
    }

    const handlePopulateForm = async () => {
      resetForm({
        values: normalizeAddressValue,
      });

      resetForm({
        touched: Object.keys(normalizeAddressValue).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {},
        ),
      });
      await validateForm();
    };

    if (!normalizeAddressIsEmpty) {
      handlePopulateForm();
    }
  }, [
    normalizeAddressAsJSON,
    resetForm,
    validateForm,
    isExpressCheckoutPayment,
    showEditing,
    isEditing,
    handleSubmit,
    customerAddress?.email,
  ]);

  const onSubmitHandler = (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    handleHeapErrorHandling();
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (isValid) {
      handleSubmit();
    } else {
      resetForm({
        values,
        touched: Object.keys(values).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {},
        ),
      });
      validateForm();
      const firstErrorFieldName = Object.entries(errors)?.[0]?.[0];
      if (firstErrorFieldName) {
        focusAndScrollToByName(firstErrorFieldName);
      }
    }
  };

  const handleOnSubmitValidator = useCallback(
    async (newAddress: CustomerAddress, moveNextStep: boolean) => {
      const data = {
        listID: 'subscribe_welcome_popup',
        authData: {
          email: values.email,
        },
        channels: {
          email: values.email,
        },
      };
      const addressDraft: CustomerAddress = {
        ...newAddress,
        additionalStreetInfo:
          !values.additionalStreetInfo ||
          newAddress.streetAddress
            .toLowerCase()
            .includes(values.additionalStreetInfo.toLowerCase())
            ? ''
            : values.additionalStreetInfo,
        email: values.email,
        phone: values.phone,
        firstName: values.firstName,
        lastName: values.lastName,
      };
      if (
        localStorage.getItem('newAddress') &&
        isDuplicateAddress(addressDraft, addresses)
      ) {
        localStorage.removeItem('newAddress');
      }
      resetForm({ values: addressDraft });
      if (moveNextStep) {
        if (signupForNewsletter) {
          publish({ subscription: 'onEmailSubscribe', parameters: data });
        }
        await onClickSave(addressDraft);
      }
      setErrorOnValidateAddress(false);
      setValidateAddress(null);
    },
    [
      addresses,
      values.additionalStreetInfo,
      values.email,
      values.phone,
      values.firstName,
      values.lastName,
      onClickSave,
      resetForm,
      signupForNewsletter,
      publish,
    ],
  );

  const cancelShippingInfoHandler = useCallback(async () => {
    resetForm({
      values: normalizeAddress,
    });
    await onClickCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(normalizeAddress), onClickCancel, resetForm]);

  const valuesASJson = JSON.stringify(values);
  useEffect(() => {
    const valuesAsValue = JSON.parse(valuesASJson) as CustomerAddress;
    const validateFieldsOfCustomerAccountAddress = async () => {
      await validateForm();
      dispatch(setNeedsToValidateFieldsOfCustomerAccountAddressDraft(false));
    };
    if (
      valuesAsValue.firstName?.trim() &&
      needsToValidateFieldsOfCustomerAccountAddressDraft
    ) {
      validateFieldsOfCustomerAccountAddress();
    }
  }, [
    dispatch,
    needsToValidateFieldsOfCustomerAccountAddressDraft,
    validateForm,
    valuesASJson,
  ]);

  useTealium({
    title: 'Verify Your Shipping Address',
    isCurrent: validateAddress || errorOnValidateAddress,
  });

  return (
    <>
      {(validateAddress || errorOnValidateAddress) && (
        <AddressValidatorModal
          firstName={values.firstName ?? ''}
          lastName={values.lastName ?? ''}
          verifiedAddress={
            validateAddress ? validateAddress?.address : undefined
          }
          currentAddress={{
            streetAddress: values.streetAddress,
            city: values.city ?? '',
            postalCode: values.postalCode ?? '',
            country: values.country,
            state: values.state ?? '',
            additionalStreetInfo: values.additionalStreetInfo ?? '',
          }}
          canVerify={
            validateAddress
              ? validateAddress.score !== '0'
              : !errorOnValidateAddress
          }
          onSubmit={handleOnSubmitValidator}
        />
      )}
      {showCompleted && (
        <div className={styles['stepped-address-summary-container']}>
          <AddressSummary
            email={values.email}
            phone={values.phone}
            firstName={values.firstName ?? ''}
            lastName={values.lastName ?? ''}
            streetAddress={values.streetAddress}
            postalCode={values.postalCode ?? ''}
            city={values.city ?? ''}
            country={values.country}
            state={values.state ?? ''}
            additionalStreetInfo={values.additionalStreetInfo}
            showContactInfo
          />
        </div>
      )}
      {showEditing && (
        <>
          <ErrorBanner
            showError={isShippingStepError}
            errorTitle={formatMessage(commonMessages.errorTitle)}
            onCloseErrorMessage={() => {
              dispatch({ type: 'removeError' });
              dispatch({ type: 'removeShippingAddressErrorCode' });
            }}
            hyperlink={
              isCountryStateNotAllowedError(errorCode ?? '')
                ? {
                    url: SLEEP_COUNTRY_CANADA_URL,
                    text: SLEEP_COUNTRY_CANADA,
                  }
                : {}
            }
          >
            {isShippingStepError
              ? formatMessage(errorMessage as MessageDescriptor)
              : ''}
          </ErrorBanner>
          <div data-testid="address-form-container-id">
            <div
              className={classnames(styles['shipping-address-form-container'])}
            >
              <Address
                prefix="shipping"
                handleChange={handleChange}
                handleBlur={handleBlur}
                values={{
                  ...values,
                  state: values.state ?? '',
                  firstName: values.firstName ?? '',
                  lastName: values.lastName ?? '',
                  streetAddress: values.streetAddress ?? '',
                  city: values.city ?? '',
                  postalCode: values.postalCode ?? '',
                  country: values.country ?? 'US',
                  additionalStreetInfo: values.additionalStreetInfo ?? '',
                }}
                isShowingContactDetails
                isAddressAutocomplete
                onAddressAutocomplete={setFieldValue}
                onFieldValueSet={setFieldValue}
                touched={touched}
                errors={errors}
                inputType={customerId ? 'hidden' : 'text'}
              />
              {showShopPayEmailCapture && signupForNewsletterPortalElement ? (
                createPortal(
                  <div
                    className={classnames(
                      styles['newsletter-selector'],
                      styles['shop-pay-newsletter-container'],
                    )}
                  >
                    <Checkbox
                      name="signupForNewsletter"
                      id="signupForNewsletter'"
                      onChange={() => {
                        setSignupForNewsletter(!signupForNewsletter);
                      }}
                      checked={signupForNewsletter}
                      label={
                        showShopPayEmailCapture
                          ? formatMessage(messages.signupForNewsletterShopPay)
                          : formatMessage(messages.signupForNewsletter)
                      }
                    />
                  </div>,
                  signupForNewsletterPortalElement,
                )
              ) : (
                <div className={styles['newsletter-selector']}>
                  <Checkbox
                    name="signupForNewsletter"
                    id="signupForNewsletter'"
                    onChange={() => {
                      setSignupForNewsletter(!signupForNewsletter);
                    }}
                    checked={signupForNewsletter}
                    label={formatMessage(messages.signupForNewsletter)}
                  />
                </div>
              )}
            </div>
            <div className={styles['shipping-buttons-container']}>
              {isCompleted && (
                <div className={styles['button-container']}>
                  <SecondaryButton
                    onClick={cancelShippingInfoHandler}
                    title={formatMessage(messages.cancelButtonText)}
                  />
                </div>
              )}
              <div className={styles['button-container']}>
                <PrimaryButton
                  onClick={onSubmitHandler}
                  isLoading={isSaving || isCartLoading}
                  title={
                    isCompleted
                      ? formatMessage(messages.updateChangesButtonText)
                      : formatMessage(messages.nextStepButtonText)
                  }
                  size='large'
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ShippingInfoStep;
